/** Print the actual Lens board. Layout adjustments release dashboard frames for pagination;
 * figures, tables, prose, colours and typography come from the rendered report itself. */
import { sanitisePrintDocument } from './customView/customLensPrint';
import { lensPrintFontCss } from './print/lensPrintFonts';
import { preserveReportCanvas } from './print/preserveReportCanvas';
import type { PrintSettings } from './print/printSettings';
import { limitPrintTables, singleSheetHtml } from './print/singleSheetPrint';

export type LensPdfErrorCode = 'busy' | 'empty' | 'missing-cards' | 'stale';
export class LensPdfExportError extends Error {
  constructor(
    readonly code: LensPdfErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'LensPdfExportError';
  }
}

export type LensBoardPrintInput = {
  root: HTMLElement;
  title: string;
  periodCurrent?: string;
  periodComparison?: string;
  filters?: ReadonlyArray<string>;
  expectedCardCount: number;
  includeHeading?: boolean;
  singlePage?: boolean;
  printAsShown?: boolean;
  print?: PrintSettings;
  isStale?: () => boolean;
  onProgress?: (done: number, total: number) => void;
  generatedAt?: Date;
};

// Copy appearance, not measured dashboard dimensions. Emotion rules can live only in CSSOM,
// and the scheduled renderer intentionally has no stylesheet or font network access.
const PRINT_PROPERTIES = [
  'display',
  'box-sizing',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'line-height',
  'letter-spacing',
  'text-align',
  'text-transform',
  'text-decoration',
  'white-space',
  'overflow-wrap',
  'word-break',
  'color',
  'background-color',
  'opacity',
  'visibility',
  'clip-path',
  'fill',
  'stroke',
  'stroke-width',
  'border-top',
  'border-right',
  'border-bottom',
  'border-left',
  'border-radius',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'flex-direction',
  'flex-wrap',
  'align-items',
  'justify-content',
  'gap',
  'flex',
  'grid-column',
  'grid-row',
  'align-self',
  'justify-self',
  'vertical-align',
  'list-style-type',
  'list-style-position',
  'border-collapse',
  'border-spacing',
];
const CARD = '[data-lens-pdf-card]';
const OMIT =
  '[data-lens-pdf-ignore], [data-testid="card-action-tray"], .lens-card-resize-handle, .react-resizable-handle';

function releaseFrame(element: HTMLElement) {
  Object.assign(element.style, {
    position: 'static',
    transform: 'none',
    width: 'auto',
    minWidth: '0',
    maxWidth: 'none',
    height: 'auto',
    minHeight: '0',
    maxHeight: 'none',
    overflow: 'visible',
  });
}

const FILTER_CONTROL = 'input, select, textarea, [role="combobox"]';

function isReportBlock(el: HTMLElement) {
  if (el.querySelector('table, canvas, [role="grid"], [role="alert"]')) return true;
  return Array.from(el.querySelectorAll<Element>('svg, img')).some((node) => {
    const box = node.getBoundingClientRect();
    return box.width > 120 && box.height > 36;
  });
}

function dropFilterChrome(source: HTMLElement, clone: HTMLElement, paired: Map<HTMLElement, HTMLElement>) {
  for (const el of Array.from(source.querySelectorAll<HTMLElement>('*'))) {
    if (!el.isConnected || el.closest('table, [role="grid"]')) continue;
    if (!el.matches(FILTER_CONTROL) && !el.querySelector(FILTER_CONTROL)) continue;
    if (isReportBlock(el)) continue;
    const parent = el.parentElement;
    if (!parent) continue;
    const besideReport = Array.from(parent.children).some(
      (child) => child !== el && child instanceof HTMLElement && isReportBlock(child),
    );
    if (!besideReport) continue;
    const copy = paired.get(el);
    if (!copy || copy === clone || !clone.contains(copy)) continue;
    copy.remove();
  }
}

/** Creates the same script-free, network-free print document used for scheduled and manual exports. */
export async function createLensBoardPrintDocument(input: LensBoardPrintInput): Promise<string> {
  const check = () => {
    if (input.isStale?.()) throw new LensPdfExportError('stale', 'The board changed while preparing print.');
  };
  check();
  await input.root.ownerDocument.fonts?.ready;
  const cards = Array.from(input.root.querySelectorAll<HTMLElement>(CARD));
  if (!cards.length) throw new LensPdfExportError('empty', 'There are no cards on the board to export.');
  if (cards.length !== input.expectedCardCount)
    throw new LensPdfExportError(
      'missing-cards',
      `Only ${cards.length} of ${input.expectedCardCount} cards are on the board.`,
    );

  const owner = input.root.ownerDocument;
  const view = owner.defaultView!;
  const doc = owner.implementation.createHTMLDocument(input.title);
  const clone = input.root.cloneNode(true) as HTMLElement;
  const originals = [input.root, ...Array.from(input.root.querySelectorAll<HTMLElement>('*'))];
  const copies = [clone, ...Array.from(clone.querySelectorAll<HTMLElement>('*'))];
  const paired = new Map(originals.map((node, index) => [node, copies[index]]));
  dropFilterChrome(input.root, clone, paired);
  for (const original of originals) {
    const copy = paired.get(original)!;
    if (copy !== clone && !clone.contains(copy)) continue;
    const appearance = view.getComputedStyle(original);
    copy.removeAttribute('style');
    for (const property of PRINT_PROPERTIES) copy.style.setProperty(property, appearance.getPropertyValue(property));
    if (appearance.display === 'grid') {
      const columns = appearance.gridTemplateColumns
        .split(/\s+/)
        .map((value) => (/^\d+(?:\.\d+)?px$/.test(value) ? parseFloat(value) : 0));
      if (columns.length && columns.every((width) => width > 0))
        copy.style.gridTemplateColumns = columns.map((width) => `${width}fr`).join(' ');
      else if (input.singlePage) copy.style.gridTemplateColumns = appearance.gridTemplateColumns;
    }
    if (input.singlePage && original.parentElement) {
      const parentStyle = view.getComputedStyle(original.parentElement);
      const parentWidth = original.parentElement.getBoundingClientRect().width;
      if (parentStyle.display === 'flex' && parentStyle.flexWrap === 'wrap' && parentWidth > 0) {
        const gap = parseFloat(parentStyle.columnGap) || 0;
        const fraction = (original.getBoundingClientRect().width + gap) / (parentWidth + gap);
        copy.style.flexBasis = `calc(${fraction * 100}% - ${(1 - fraction) * gap}px)`;
        copy.style.flexGrow = '0';
        copy.style.flexShrink = '0';
      }
    }
    // Preserve the available Admin font's fallback when the isolated printer cannot fetch fonts.
    copy.style.fontFamily = `${appearance.fontFamily || 'Arial'}, Arial, sans-serif`;
    if (
      appearance.display === 'none' ||
      appearance.visibility === 'hidden' ||
      appearance.visibility === 'collapse' ||
      appearance.opacity === '0' ||
      appearance.clipPath === 'inset(50%)' ||
      /rect\(0px[, ]+0px[, ]+0px[, ]+0px\)/.test(appearance.clip) ||
      original.matches(`${OMIT}, details:not([open])`)
    ) {
      copy.remove();
      continue;
    }
    // Internal DOM figures encode meaning in positioned bars and small SVGs. Keep
    // that geometry while dashboard/card frames are released separately below.
    if (original instanceof view.SVGElement && original.tagName.toLowerCase() === 'svg') {
      copy.style.width = appearance.width;
      copy.style.height = appearance.height;
    }
    if (appearance.position === 'absolute') {
      copy.style.position = 'absolute';
      const width = (original.offsetParent ?? original.parentElement)?.getBoundingClientRect().width ?? 0;
      for (const property of ['width', 'left', 'right']) {
        const value = appearance.getPropertyValue(property);
        const pixels = /^-?\d+(?:\.\d+)?px$/.test(value) ? parseFloat(value) : null;
        copy.style.setProperty(
          property,
          width && pixels !== null && Math.abs(pixels) > 1 ? `${(pixels / width) * 100}%` : value,
        );
      }
      for (const property of ['height', 'top', 'bottom'])
        copy.style.setProperty(property, appearance.getPropertyValue(property));
    } else if (
      appearance.position === 'relative' &&
      Array.from(original.children).some((child) => {
        const style = view.getComputedStyle(child);
        return (
          style.position === 'absolute' && style.display !== 'none' && style.opacity !== '0' && !child.matches(OMIT)
        );
      })
    ) {
      copy.style.position = 'relative';
      copy.style.height = appearance.height;
    }
    if (original instanceof view.HTMLCanvasElement) {
      if (!original.width || !original.height) throw new Error('A chart has not finished drawing.');
      const image = doc.createElement('img');
      image.src = original.toDataURL('image/png');
      image.alt = original.getAttribute('aria-label') || 'Report chart';
      image.style.cssText = copy.style.cssText;
      image.style.position = 'static';
      image.style.inset = 'auto';
      image.style.width = '100%';
      image.style.height = 'auto';
      image.style.aspectRatio = `${original.width} / ${original.height}`;
      copy.replaceWith(image);
      // ECharts absolutely positions its canvas inside measured wrappers. The
      // bitmap now participates in flow, so those wrappers must size from it.
      let parent = image.parentElement;
      while (parent && parent !== clone) {
        releaseFrame(parent);
        if (parent.hasAttribute('data-lens-card-body')) break;
        parent = parent.parentElement;
      }
    } else if (original instanceof view.HTMLImageElement) {
      if (!original.complete || !original.naturalWidth) throw new Error('A report image has not finished loading.');
      const bitmap = owner.createElement('canvas');
      bitmap.width = original.naturalWidth;
      bitmap.height = original.naturalHeight;
      const context = bitmap.getContext('2d');
      if (!context) throw new Error('A report image could not be copied.');
      context.drawImage(original, 0, 0);
      copy.setAttribute('src', bitmap.toDataURL('image/png'));
      copy.removeAttribute('srcset');
    }
    if (/(auto|scroll)/.test(`${appearance.overflowX} ${appearance.overflowY}`)) releaseFrame(copy);
  }
  for (const grid of Array.from(clone.querySelectorAll<HTMLElement>('.MuiDataGrid-root'))) {
    const table = doc.createElement('table');
    const head = table.createTHead().insertRow();
    const body = table.createTBody();
    const cell = (source: HTMLElement, tag: 'th' | 'td') => {
      const target = doc.createElement(tag);
      target.style.cssText = source.style.cssText;
      target.style.display = 'table-cell';
      target.style.position = 'static';
      target.style.padding = '6px 8px';
      target.style.lineHeight = '1.4';
      target.append(...Array.from(source.childNodes));
      return target;
    };
    grid.querySelectorAll<HTMLElement>('[role="columnheader"]').forEach((source) => head.append(cell(source, 'th')));
    grid.querySelectorAll<HTMLElement>('[role="row"]').forEach((source) => {
      const cells = Array.from(source.querySelectorAll<HTMLElement>('[role="gridcell"]'));
      if (cells.length) body.insertRow().append(...cells.map((source) => cell(source, 'td')));
    });
    const caption = table.createCaption();
    caption.textContent = `Table preview: ${body.rows.length} visible rows. Open the report for full detail.`;
    caption.style.cssText = 'caption-side:bottom;text-align:left;font-size:11px;padding:6px 0';
    grid.replaceWith(table);
  }
  for (const grid of Array.from(clone.querySelectorAll<HTMLElement>('*'))) {
    if (grid.style.display !== 'grid' || grid.querySelectorAll(':scope > *').length > 1) continue;
    grid.style.gridTemplateColumns = 'minmax(0, 1fr)';
  }
  clone
    .querySelectorAll('button, input, select, textarea, [role="tooltip"], .MuiTableSortLabel-icon, .MuiTabs-indicator')
    .forEach((node) => {
      // Sort headers are real report labels, not paper controls.
      if (node.matches('button') && node.closest('th')) return;
      // Reports put data labels (room names, regions, legend entries) in clickable buttons.
      // On paper those are content, so print the label and drop the control. Real controls
      // in a table cell opt out with data-lens-pdf-ignore, which the copy loop already removed.
      // A span keeps the copied print styles so swatches and spacing still lay out.
      if (
        node.matches('button') &&
        (node.closest('td, [role="gridcell"], [role="cell"]') || node.hasAttribute('data-lens-pdf-keep'))
      ) {
        const label = doc.createElement('span');
        for (const { name, value } of Array.from(node.attributes)) {
          if (name !== 'type') {
            label.setAttribute(name, value);
          }
        }
        label.append(...Array.from(node.childNodes));
        node.replaceWith(label);
        return;
      }
      node.remove();
    });
  clone
    .querySelectorAll<HTMLElement>(
      '[data-lens-card-frame], [data-lens-card-content], [data-lens-pdf-card], .MuiCard-root, .MuiCardContent-root, .MuiTableContainer-root',
    )
    .forEach(releaseFrame);
  clone
    .querySelectorAll<HTMLElement>('[data-lens-card-body], .MuiCard-root, .MuiCardContent-root')
    .forEach((element) => {
      releaseFrame(element);
      element.style.display = 'block';
    });
  // Tables need their complete row flow; preserve their visible row-limit notices too.
  for (const table of Array.from(clone.querySelectorAll('table'))) {
    if (input.singlePage) {
      table.style.width = 'max-content';
      table.style.minWidth = 'max-content';
      table.style.maxWidth = 'none';
    }
    let parent = table.parentElement;
    while (parent && parent !== clone) {
      releaseFrame(parent);
      parent.style.maxWidth = 'none';
      if (parent.matches(CARD)) break;
      parent = parent.parentElement;
    }
  }
  let landscape = Array.from(clone.querySelectorAll('table')).some(
    (table) => table.querySelectorAll('thead tr:first-child th').length > 7,
  );
  for (const grid of Array.from(input.root.querySelectorAll<HTMLElement>('.react-grid-layout'))) {
    const printed = paired.get(grid)!;
    releaseFrame(printed);
    printed.style.display = 'grid';
    printed.style.gridTemplateColumns = 'repeat(12, minmax(0, 1fr))';
    printed.style.gap = '3mm';
    printed.style.alignItems = 'start';
    const bounds = grid.getBoundingClientRect();
    const frames = Array.from(grid.children).filter((frame): frame is HTMLElement => frame instanceof view.HTMLElement);
    frames.sort(
      (a, b) =>
        a.getBoundingClientRect().top - b.getBoundingClientRect().top ||
        a.getBoundingClientRect().left - b.getBoundingClientRect().left,
    );
    for (const frame of frames) {
      const item = paired.get(frame)!;
      const rect = frame.getBoundingClientRect();
      const span = bounds.width ? Math.max(1, Math.min(12, Math.round((rect.width / bounds.width) * 12))) : 12;
      const start = bounds.width
        ? Math.max(1, Math.min(13 - span, Math.round(((rect.left - bounds.left) / bounds.width) * 12) + 1))
        : 1;
      releaseFrame(item);
      item.style.gridColumn = `${start} / span ${span}`;
      if (span < 12) landscape = true;
      // A long table uses the page width instead of becoming tiny beside another card.
      if (frame.querySelector('table')) item.style.gridColumn = '1 / -1';
      printed.append(item);
    }
  }
  releaseFrame(clone);
  // Block flow fragments across pages; a flex root can move an entire section
  // to the next sheet even when its first row fits beside the title.
  clone.style.display = 'flow-root';
  clone.style.marginTop = '0';
  const base = view.getComputedStyle(input.root);
  doc.body.style.fontFamily = `${base.fontFamily || 'Arial'}, Arial, sans-serif`;
  doc.body.style.color = base.color;
  const heading = doc.createElement('h1');
  heading.textContent = input.title || 'Lens';
  heading.style.cssText =
    'font-size:24px;line-height:1.2;margin:0 0 2mm;font-weight:600;text-decoration:none;border:0;display:block';
  const summary = doc.createElement('p');
  summary.textContent = [
    input.periodCurrent,
    input.periodComparison ? `Compared with ${input.periodComparison}` : '',
    ...(input.filters ?? []),
  ]
    .filter(Boolean)
    .join(' · ');
  summary.style.cssText = 'font-size:12px;line-height:1.5;margin:0 0 4mm';
  const generated = doc.createElement('p');
  generated.textContent = `Generated ${new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', dateStyle: 'medium', timeStyle: 'short' }).format(input.generatedAt ?? new Date())} Sydney time`;
  generated.style.cssText = 'font-size:10px;line-height:1.4;margin:0 0 3mm';
  const titleKey = (value: string) =>
    value
      .toLowerCase()
      .trim()
      .replace(/\s+dashboard$/, '')
      .replace(/[^a-z0-9]/g, '');
  const hasReportHeading =
    input.singlePage &&
    Array.from(
      clone.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6, [role="heading"], .MuiTypography-root'),
    ).some((element) => titleKey(element.textContent ?? '') === titleKey(input.title || 'Lens'));
  if (input.includeHeading !== false && !hasReportHeading) doc.body.append(heading, summary);
  doc.body.append(clone);
  if (input.includeHeading !== false) doc.body.append(generated);
  const style = doc.createElement('style');
  style.textContent = `
    @page { ${input.singlePage ? 'size: 1600px 1400px; margin: 0' : `size: A4 ${landscape ? 'landscape' : 'portrait'}; margin: 6mm`}; }
    html, body { margin: 0; padding: 0; width: auto; min-height: 0; height: auto; background: #fff; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    h1, h2, h3, h4 { break-after: avoid; text-decoration: none; }
    [data-lens-pdf-section] { margin-bottom: 4mm; }
    [data-lens-pdf-card]:not([data-lens-pdf-card-type="table"]):not([data-lens-pdf-card-type="insight"]) { break-inside: avoid; }
    .MuiPaper-root:has(img):not(:has(table)),
    section:has(img):not(:has(table)) { break-inside: avoid !important; }
    table { ${input.singlePage ? 'width: max-content; min-width: max-content' : 'width: 100%'}; table-layout: auto; border-collapse: collapse; }
    thead { display: table-header-group; } tfoot { display: table-footer-group; }
    tr { break-inside: avoid; }
    th, td { ${input.singlePage ? 'overflow-wrap: normal !important; word-break: normal !important' : 'white-space: normal; overflow-wrap: anywhere'}; }
    [data-lens-print-fit] { width: max-content; min-width: 1600px; }
    img, svg { max-width: 100%; height: auto; break-inside: avoid; }
  `;
  doc.head.append(style);
  const sharedStyles = new Map<string, string>();
  for (const element of doc.querySelectorAll<HTMLElement>('table [style]')) {
    const declarations = element.style.cssText;
    let className = sharedStyles.get(declarations);
    if (!className) {
      className = `lens-print-style-${sharedStyles.size}`;
      sharedStyles.set(declarations, className);
    }
    element.classList.add(className);
    element.removeAttribute('style');
  }
  const sharedStyle = doc.createElement('style');
  sharedStyle.textContent = Array.from(
    sharedStyles,
    ([declarations, className]) => `.${className}{${declarations}}`,
  ).join('\n');
  doc.head.append(sharedStyle);
  cards.forEach((_card, index) => input.onProgress?.(index + 1, cards.length));
  const fontCss = /Suisse/.test(doc.documentElement.outerHTML) ? await lensPrintFontCss() : '';
  const workbook = input.singlePage && Boolean(input.root.querySelector('.lp-workbook'));
  const workbookBoxes = workbook ? Array.from(input.root.querySelectorAll<HTMLElement>('.lp-table-scroll')) : [];
  const scrollBoxes = input.printAsShown
    ? Array.from(input.root.querySelectorAll<HTMLElement>('*')).filter(
        (element) =>
          workbookBoxes.includes(element) ||
          (/auto|scroll/.test(view.getComputedStyle(element).overflowY) &&
            element.scrollHeight > element.clientHeight + 1),
      )
    : workbookBoxes;
  for (const original of scrollBoxes) {
    const copy = paired.get(original);
    if (!copy) continue;
    const bounds = original.getBoundingClientRect();
    for (const table of Array.from(original.querySelectorAll('table'))) {
      const twin = paired.get(table);
      if (twin) {
        const width = `${table.getBoundingClientRect().width}px`;
        Object.assign(twin.style, { width, minWidth: width, maxWidth: width });
      }
    }
    const height = workbookBoxes.includes(original) ? Math.min(420, bounds.height) : original.clientHeight;
    const visibleRows = Array.from(original.querySelectorAll('tr')).filter((row) => {
      const box = row.getBoundingClientRect();
      return box.bottom - bounds.top <= height + 1;
    });
    const bottom = visibleRows[visibleRows.length - 1]?.getBoundingClientRect().bottom;
    const visibleHeight = bottom ? Math.min(height, bottom - bounds.top) : height;
    Object.assign(copy.style, { height: `${visibleHeight}px`, maxHeight: `${visibleHeight}px`, overflow: 'hidden' });
    if (input.printAsShown) continue;
    const note = doc.createElement('p');
    note.textContent = 'Table preview: visible dashboard area. Open the report for full detail.';
    note.style.cssText = 'font:11px Arial;margin:6px 0';
    copy.after(note);
  }
  if (input.singlePage && !workbook && !input.printAsShown) limitPrintTables(clone);
  preserveReportCanvas(input.root, paired);
  check();
  const html = sanitisePrintDocument(`<!doctype html>${doc.documentElement.outerHTML}`, input.title, fontCss);
  return input.singlePage ? singleSheetHtml(html, owner, input.print) : html;
}
